/**
 * Group create / edit dialog.
 *
 * - Create: takes a stable group_id (lowercase + dashes/underscores),
 *   name, description, permissions checklist.
 * - Edit: same fields except group_id (immutable). The "admin" group's
 *   permissions are read-only (the backend refuses the update).
 *
 * Members management lives in a separate dialog (GroupMembersDialog)
 * so this stays focused on metadata.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/cn";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usersApi, type GroupSummary, type ResourceCatalogEntry } from "@/lib/api-users";

// Standard CRUD actions appear FIRST in the matrix in this order, so
// every resource lines up on the same columns regardless of which
// subset it supports. Anything else (activate, rotate, reload, …)
// flows into the "Other" column on the right with a small badge —
// they're rare enough that giving each one its own column would
// waste horizontal space.
//
// `delete` is a SEPARATE, destructive permission (not implied by
// write): it gates destroying a top-level entity (a source / dataset /
// workflow / app / mapping / credential / group). Removing sub-parts
// (a column, a member, a sample) stays under `write`. Only resources
// with a top-level DELETE route expose the column (others render "·").
const STANDARD_ACTIONS = ["read", "write", "execute", "delete"] as const;

/** Matrix sections = Tray's product PLANES, the same five rail
 *  sections as the sidebar. The mapping comes from the BACKEND
 *  catalog (each resource carries its ``group``), so a new resource
 *  lands in the right section without touching this file — only the
 *  display order + labels live here. An unknown group falls through
 *  to "Other" (a backend ahead of this frontend). */
const PLANE_ORDER = ["data", "flows", "apps", "odino", "admin"] as const;
const PLANE_LABELS: Record<string, string> = {
  data: "Dati",
  flows: "Integrazione",
  apps: "Apps",
  odino: "Odino",
  admin: "Amministrazione",
};

/** Build the plain ``resource:action`` permission string the backend
 *  expects. Tags are NOT a security concept — the old scoped form
 *  (``resource[tag=X]:action``) was removed; tags remain purely
 *  organizational labels. */
function buildPermKey(resource: string, action: string): string {
  return `${resource}:${action}`;
}

/**
 * Convert a possibly-mixed permissions list (strings + legacy
 * 2-tuples) to canonical "resource:action" strings the picker can
 * compare directly.
 */
function normalizePermissions(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const entry of raw) {
    if (typeof entry === "string") {
      out.push(entry);
    } else if (Array.isArray(entry) && entry.length === 2) {
      const [resource, action] = entry;
      if (typeof resource === "string" && typeof action === "string") {
        out.push(`${resource}:${action}`);
      }
    }
  }
  return out;
}

export type GroupDialogMode =
  | { kind: "create" }
  | { kind: "edit"; group: GroupSummary };

export interface GroupDialogProps {
  open: boolean;
  mode: GroupDialogMode;
  onOpenChange: (open: boolean) => void;
}

export function GroupDialog({ open, mode, onOpenChange }: GroupDialogProps) {
  const qc = useQueryClient();
  const isCreate = mode.kind === "create";
  const editing = mode.kind === "edit" ? mode.group : null;
  const isAdminGroup = editing?.group_id === "admin";

  const [groupId, setGroupId] = React.useState("");
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [perms, setPerms] = React.useState<Set<string>>(new Set());

  const knownQuery = useQuery({
    queryKey: ["known-permissions"],
    queryFn: () => usersApi.knownPermissions(),
    enabled: open,
  });

  React.useEffect(() => {
    if (!open) return;
    if (editing) {
      setGroupId(editing.group_id);
      setName(editing.name);
      setDescription(editing.description ?? "");
      // Normalise defensively: the backend should always send strings
      // (the GroupSummary validator converts legacy tuple rows to
      // canonical strings on the way out), but if any legacy entry
      // sneaks through, the .has() check would silently miss every
      // checkbox and the dialog would look like the group had no
      // permissions saved. Convert anything tuple-shaped to the
      // canonical "resource:action" string here too.
      setPerms(new Set(normalizePermissions(editing.permissions)));
    } else {
      setGroupId("");
      setName("");
      setDescription("");
      setPerms(new Set());
    }
  }, [open, editing]);

  // Drop grants whose resource is no longer in the vocabulary (e.g.
  // ``connector:*`` / ``pipeline:*`` / ``mapping:read`` from a removed
  // surface) before saving — otherwise the backend 422s and the
  // operator can't save. Opening + saving an old group thus cleans it
  // up. ``*`` (the admin wildcard) is always kept. Skip the filter
  // until the catalog has loaded, so we never blank a valid set.
  const cleanedPermissions = (): string[] => {
    const known = knownQuery.data?.resources;
    if (!known) return Array.from(perms);
    const knownResources = new Set(known.map((r) => r.resource));
    return Array.from(perms).filter((p) => {
      const resource = p.split(":")[0];
      return resource === "*" || knownResources.has(resource);
    });
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      if (isCreate) {
        return usersApi.createGroup({
          group_id: groupId.trim(),
          name: name.trim(),
          description: description.trim() || null,
          permissions: cleanedPermissions(),
        });
      }
      return usersApi.updateGroup(editing!.group_id, {
        name: name.trim(),
        description: description.trim() || null,
        permissions: isAdminGroup ? undefined : cleanedPermissions(),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["groups"] });
      onOpenChange(false);
    },
  });

  const canSubmit =
    (isCreate ? groupId.trim().length > 0 : true) &&
    name.trim().length > 0 &&
    !saveMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Bumped to ``max-w-3xl`` — the permissions matrix has 18
          resources × 5 columns + the scope sub-rows, which felt
          claustrophobic at the default ``max-w-lg``. The Cancel/
          Save footer stays where it is; the body just gets more
          room to breathe. */}
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{isCreate ? "New group" : `Edit group: ${editing!.name}`}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {isCreate && (
            <div className="space-y-1.5">
              <Label htmlFor="group-id">Group ID</Label>
              <Input
                id="group-id"
                value={groupId}
                onChange={(e) => setGroupId(e.target.value.toLowerCase())}
                placeholder="operator"
                className="font-mono text-xs"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                Lowercase letters, digits, dashes and underscores. Immutable after creation.
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="group-name">Display name</Label>
            <Input
              id="group-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Operator"
              autoFocus={!isCreate}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="group-desc">Description</Label>
            <Input
              id="group-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this group is allowed to do"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Permissions</Label>
            {isAdminGroup ? (
              <Alert tone="info">
                The <strong>admin</strong> group is special — its
                permissions cannot be edited (members bypass every
                permission check).
              </Alert>
            ) : !knownQuery.data ? (
              <div className="text-xs text-muted-foreground">Loading…</div>
            ) : (
              <PermissionMatrix
                resources={knownQuery.data.resources}
                selected={perms}
                onChange={setPerms}
              />
            )}
          </div>

          {saveMutation.isError && (
            <Alert tone="danger">
              {(saveMutation.error as Error)?.message ?? "Save failed"}
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saveMutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={() => saveMutation.mutate()} disabled={!canSubmit}>
            {saveMutation.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            )}
            {isCreate ? "Create" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// PermissionMatrix — F6b
// ---------------------------------------------------------------------------
//
// Renders the catalog as a resource × action grid. ``read``, ``write``,
// ``execute``, ``delete`` get fixed columns (left-aligned with the
// header) so it's easy to scan vertically: "what can this group
// read?" → look down the read column. Resource-specific verbs
// (``activate``, ``rotate``, ``reload``) go in the "Other" column as
// small inline checkboxes with the verb label printed next to them —
// they're rare so spending a column each would waste space.
//
// The component is fully controlled: parent owns the ``selected``
// Set of canonical permission strings and the ``onChange`` callback
// produces a fresh Set. We never mutate the input.

interface PermissionMatrixProps {
  resources: ResourceCatalogEntry[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}

function PermissionMatrix({
  resources,
  selected,
  onChange,
}: PermissionMatrixProps) {
  /** Toggle a single ``resource:action`` permission, enforcing the
   *  action hierarchy: any non-read action IMPLIES ``read`` (you can't
   *  write/execute what you can't see — flagging write without read is
   *  meaningless). Mirrors backend ``check_permission``:
   *   - ticking a non-read action auto-grants ``read``;
   *   - unticking ``read`` clears every action on that resource, since
   *     none can stand without it. */
  function toggle(resource: string, action: string, on: boolean) {
    const key = buildPermKey(resource, action);
    const next = new Set(selected);
    if (on) {
      next.add(key);
      if (action !== "read") next.add(buildPermKey(resource, "read"));
    } else {
      next.delete(key);
      if (action === "read") {
        for (const k of [...next]) {
          if (k.startsWith(`${resource}:`)) next.delete(k);
        }
      }
    }
    onChange(next);
  }

  // Group resources by their backend-declared plane, in the sidebar's
  // rail order. A group this frontend doesn't know yet falls through
  // to "Other" so a newer backend still renders fully.
  const grouped: Array<{ label: string; rows: ResourceCatalogEntry[] }> = [];
  for (const plane of PLANE_ORDER) {
    const rows = resources.filter((r) => r.group === plane);
    if (rows.length > 0) grouped.push({ label: PLANE_LABELS[plane], rows });
  }
  const orphans = resources.filter(
    (r) => !(PLANE_ORDER as readonly string[]).includes(r.group),
  );
  if (orphans.length > 0) grouped.push({ label: "Other", rows: orphans });

  return (
    <div className="overflow-hidden rounded-md border border-border/60 bg-muted/30">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-muted/80 text-muted-foreground backdrop-blur">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Resource</th>
            {STANDARD_ACTIONS.map((a) => (
              <th
                key={a}
                className="w-20 px-2 py-2 text-center font-medium capitalize"
              >
                {a}
              </th>
            ))}
            <th className="px-2 py-2 text-left font-medium">Other</th>
          </tr>
        </thead>
        {grouped.map((section, sectionIdx) => (
          <tbody key={section.label}>
            {/* Section header — a tinted row spanning the full width.
                Visually breaks the matrix into the logical groupings
                without being a separate table (so column alignment
                stays consistent across sections). */}
            <tr className="bg-muted/40">
              <td
                colSpan={STANDARD_ACTIONS.length + 2}
                className={cn(
                  "px-3 py-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-foreground",
                  sectionIdx === 0 ? "border-t-0" : "border-t border-border/60",
                )}
              >
                {section.label}
              </td>
            </tr>
            {section.rows.map((row) => (
              <MatrixRow
                key={row.resource}
                resource={row}
                selected={selected}
                onToggle={toggle}
              />
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
}


/** Single row in the matrix — one resource, a checkbox per action. */
interface MatrixRowProps {
  resource: ResourceCatalogEntry;
  selected: Set<string>;
  onToggle: (resource: string, action: string, on: boolean) => void;
}

function MatrixRow({ resource, selected, onToggle }: MatrixRowProps) {
  const otherActions = resource.actions.filter(
    (a) => !STANDARD_ACTIONS.includes(a as (typeof STANDARD_ACTIONS)[number]),
  );
  // read is the implied base of every other action: lock its checkbox
  // while any non-read action on this resource is granted, so it can't
  // be unticked out from under them (untick the others first).
  const readLocked = resource.actions.some(
    (a) => a !== "read" && selected.has(buildPermKey(resource.resource, a)),
  );
  return (
    <tr className="border-t border-border/40 hover:bg-accent/5">
      <td className="px-3 py-1.5 font-mono">{resource.resource}</td>
      {STANDARD_ACTIONS.map((action) => {
        const supported = resource.actions.includes(action);
        if (!supported) {
          return (
            <td key={action} className="px-2 py-1.5 text-center text-muted-foreground/40">
              ·
            </td>
          );
        }
        const key = buildPermKey(resource.resource, action);
        const locked = action === "read" && readLocked;
        return (
          <td key={action} className="px-2 py-1.5 text-center">
            <input
              type="checkbox"
              aria-label={key}
              checked={selected.has(key)}
              disabled={locked}
              title={locked ? "Implicito: un'altra azione richiede 'read'" : undefined}
              className={cn(locked && "opacity-60")}
              onChange={(e) => onToggle(resource.resource, action, e.target.checked)}
            />
          </td>
        );
      })}
      <td className="px-2 py-1.5">
        {otherActions.length === 0 ? (
          <span className="text-muted-foreground/40">—</span>
        ) : (
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {otherActions.map((action) => {
              const key = buildPermKey(resource.resource, action);
              return (
                <label
                  key={action}
                  className="inline-flex cursor-pointer items-center gap-1"
                >
                  <input
                    type="checkbox"
                    aria-label={key}
                    checked={selected.has(key)}
                    onChange={(e) =>
                      onToggle(resource.resource, action, e.target.checked)
                    }
                  />
                  <span className="font-mono">{action}</span>
                </label>
              );
            })}
          </div>
        )}
      </td>
    </tr>
  );
}
